export interface ApiErrorPayload {
  code?: string;
  message?: string;
  [key: string]: unknown;
}

const parseJson = (value: string): ApiErrorPayload | null => {
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' ? (parsed as ApiErrorPayload) : null;
  } catch {
    return null;
  }
};

const coerceMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '';
};

export const parseApiError = (error: unknown): ApiErrorPayload | null => {
  const message = coerceMessage(error).trim();
  if (!message || message.length > 4_096) {
    return null;
  }

  const payload = parseJson(message);
  if (payload) {
    return payload;
  }

  // Some endpoints return "CODE: message" style errors.
  const colonIndex = message.indexOf(':');
  if (colonIndex > 0) {
    return {
      code: message.slice(0, colonIndex).trim(),
      message: message.slice(colonIndex + 1).trim(),
    };
  }

  return { message };
};

export const getApiErrorCode = (error: unknown): string | null => {
  const payload = parseApiError(error);
  return payload?.code ?? null;
};

export const getApiErrorMessage = (error: unknown): string | null => {
  const payload = parseApiError(error);
  return payload?.message ?? null;
};

export const isAbortError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    if (error.name === 'AbortError' || error.code === DOMException.ABORT_ERR) {
      return true;
    }
  }

  if (error instanceof Error && error.name === 'AbortError') {
    return true;
  }

  if (typeof error === 'object' && error !== null) {
    const candidate = error as { name?: unknown; code?: unknown };
    if (candidate.name === 'AbortError') {
      return true;
    }
    if (candidate.code === 'ABORT_ERR') {
      return true;
    }
  }

  return false;
};
